import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { Hit } from "../../../../data-model/algoliaSearchModel";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import {
  capitalizeString,
  capitalizeWordsSeparatedBySpace,
} from "../../../../utils/stringModifications";
// Algolia table column keys for medical PA orders
export enum MedicalPaOrdersAlgoliaTableColumnKeys {
  SELECT = "select",
  PATIENT_DETAILS = "patientDetails",
  CREATED_AT = "createdAt",
  DATE_OF_WORK = "dateOfWork",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  PROVIDER = "provider",
  DATE_OF_SERVICE = "dateOfService",
  BO_VALUE = "boValue",
  AUTH_VERIFICATION = "authVerification",
  RPA_STATUS = "rpaStatusForAlgolia",
  ID = "id",
  ROW_DATA = "rowData",
  ASSIGNEE = "assignee",
  RPA_STATUS_TWO = "rpaStatusTwoForAlgolia",
}

export const MedicalPaOrdersAlgoliaTableHeadersForAdmin: TableHeader[] = [
  {
    label: "All",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.SELECT,
    order: 0,
    width: 4,
    type: TableCellType.CHECKBOX,
  },
  {
    label: "Patient Name",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 11,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Creation",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT,
    order: 2,
    width: 11,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "DOS",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE,
    order: 3,
    width: 12,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Plan Details",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Medication",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION,
    order: 5,
    width: 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Provider",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER,
    order: 6,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "BO Value",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE,
    order: 7,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: "Assigned To",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE,
    order: 9,
    width: 10,
    type: TableCellType.CLICKABLE_BADGE,
    subKey: "text",
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 5,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
];

export const MedicalPaOrdersAlgoliaTableHeadersForNonAdmin: TableHeader[] = [
  {
    label: "Patient Name",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 12,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Creation",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT,
    order: 2,
    width: 11,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "DOS",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE,
    order: 3,
    width: 12,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Plan Details",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS,
    order: 4,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Medication",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION,
    order: 5,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Provider",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER,
    order: 6,
    width: 13,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "BO Value",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE,
    order: 7,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: "Assigned To",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE,
    order: 9,
    width: 10,
    type: TableCellType.STRING,
    subKey: "text",
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 5,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
];

// Generate table data from Algolia Hit[] for admin users
export const generateMedicalPaOrdersAlgoliaTableDataForAdmin = (
  data: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return data.map((item) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.master_auth_status,
    ) || {
      id: "not_available",
      text: "Not Available",
      textColor: "#0F0F0F",
      bgColor: "#F5F5F5",
    };

    return {
      [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
        id: item?.id ?? "",
        label: "",
        isChecked: false,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(item?.first_name ?? "", "", item?.last_name ?? ""),
        ),
        secondaryText: `MRN : ${item?.patient_id ?? ""}`,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE]: moment(
        item?.date_of_service_iso,
      ).format("MM/DD/YYYY"),
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK]:
        item?.date_of_work_iso ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS]: {
        mainText: item?.primary_payer_name ?? "",
        secondaryText: item?.primary_member_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION]:
        item?.regimen_name ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA]: JSON.stringify(item),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ID]: item?.id ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT]: moment(
        item?.created_at_iso ?? "",
      ).format("MM/DD/YYYY"),
      [MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === "denied" || authStatus?.id === "denied_by_risa",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE]: {
        text: capitalizeWordsSeparatedBySpace(item?.bo_status ?? "Required"),
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS]: {
        status: item?.ev_write_back_status ?? EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          item?.document_upload_status ?? DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        item?.practitioner_name ?? "",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE]: {
        text: item?.assigned_to_name ?? "",
        id: item?.id ?? "",
        shouldLogEvent: true,
        assignedToId: item?.assigned_to ?? "",
      },
    };
  });
};

// Generate table data from Algolia Hit[] for non-admin users
export const generateMedicalPaOrdersAlgoliaTableDataForNonAdmin = (
  data: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return data.map((item) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.master_auth_status,
    ) || {
      id: "not_available",
      text: "Not Available",
      textColor: "#0F0F0F",
      bgColor: "#F5F5F5",
    };

    return {
      [MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(item?.first_name ?? "", "", item?.last_name ?? ""),
        ),
        secondaryText: `MRN : ${item?.patient_id ?? ""}`,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE]: moment(
        item?.date_of_service_iso,
      ).format("MM/DD/YYYY"),
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK]:
        item?.date_of_work_iso ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS]: {
        mainText: item?.primary_payer_name ?? "",
        secondaryText: item?.primary_member_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION]:
        item?.regimen_name ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA]: JSON.stringify(item),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ID]: item?.id ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT]: moment(
        item?.created_at_iso ?? "",
      ).format("MM/DD/YYYY"),
      [MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === "denied" || authStatus?.id === "denied_by_risa",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE]: {
        text: capitalizeWordsSeparatedBySpace(item?.bo_status ?? "Required"),
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        item?.practitioner_name ?? "",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE]: capitalizeString(
        item?.assigned_to_name ?? "--",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS]: {
        status: item?.ev_write_back_status ?? EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          item?.document_upload_status ?? DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
      },
    };
  });
};
